import type { AppRouteContract, RouteMatch } from '@beautyburst/routing';
import { resolveRouteIn } from '@beautyburst/routing';
import { customerBasePath } from './base-path.ts';
import type { AppBasePath } from '@beautyburst/tokens';

export type { RouteMatch } from '@beautyburst/routing';
export { buildPath, normalizePath, duplicatePaths } from '@beautyburst/routing';

/**
 * Customer surface route contract.
 *
 * The route table is data rather than JSX so navigation, feature-flag gating and
 * the authorization expectation for each screen can be asserted without
 * rendering anything.
 */
export interface RouteContract extends AppRouteContract {
  flag: string | null;
  /** Which bottom-navigation entry should read as current on this screen. */
  navKey: 'home' | 'shop' | 'stores' | 'search' | 'orders' | 'account' | null;
}

export const CUSTOMER_ROUTES: readonly RouteContract[] = [
  { path: '/', screen: 'Home', permission: null, flag: 'customer.marketplace', navKey: 'home' },
  {
    path: '/search',
    screen: 'Search results',
    permission: null,
    flag: 'customer.search',
    navKey: 'search',
  },
  {
    path: '/categories',
    screen: 'Categories',
    permission: null,
    flag: 'customer.marketplace',
    navKey: 'shop',
  },
  {
    path: '/categories/:categorySlug',
    screen: 'Category',
    permission: null,
    flag: 'customer.marketplace',
    navKey: 'shop',
  },
  {
    path: '/stores',
    screen: 'Stores',
    permission: null,
    flag: 'customer.marketplace',
    navKey: 'stores',
  },
  {
    path: '/stores/:storeSlug',
    screen: 'Store front',
    permission: null,
    flag: 'customer.marketplace',
    navKey: 'stores',
  },
  {
    path: '/products/:productSlug',
    screen: 'Product detail',
    permission: null,
    flag: 'customer.marketplace',
    navKey: 'shop',
  },
  {
    path: '/after-hours',
    screen: 'Beauty After Hours',
    permission: null,
    flag: 'customer.after_hours_discovery',
    navKey: 'shop',
  },
  {
    path: '/beauty-burst-plus',
    screen: 'Beauty Burst Plus',
    permission: null,
    flag: 'membership.program',
    navKey: null,
  },
  {
    path: '/partner',
    screen: 'Retailer partnership',
    permission: null,
    flag: 'retailer.lead_capture',
    navKey: null,
  },
  { path: '/cart', screen: 'Cart', permission: null, flag: 'customer.cart', navKey: null },
  { path: '/sign-in', screen: 'Sign in', permission: null, flag: null, navKey: 'account' },
  { path: '/sign-up', screen: 'Sign up', permission: null, flag: null, navKey: 'account' },
  {
    path: '/forgot-password',
    screen: 'Forgot password',
    permission: null,
    flag: null,
    navKey: 'account',
  },
  {
    path: '/reset-password',
    screen: 'Forgot password',
    permission: null,
    flag: null,
    navKey: 'account',
  },
  {
    path: '/accept-invitation',
    screen: 'Accept invitation',
    permission: null,
    flag: null,
    navKey: 'account',
  },
  {
    path: '/account',
    screen: 'Account',
    permission: 'customer.account.manage',
    flag: 'customer.account',
    navKey: 'account',
  },
  {
    path: '/account/favorites',
    screen: 'Favorites',
    permission: 'customer.favorites.manage',
    flag: 'customer.favorites',
    navKey: 'account',
  },
  {
    path: '/account/lists',
    screen: 'Shopping lists',
    permission: 'customer.lists.manage',
    flag: 'customer.shopping_lists',
    navKey: 'account',
  },
  {
    path: '/account/lists/:listId',
    screen: 'Shopping list detail',
    permission: 'customer.lists.manage',
    flag: 'customer.shopping_lists',
    navKey: 'account',
  },
  {
    path: '/account/addresses',
    screen: 'Addresses',
    permission: 'customer.address.manage',
    flag: 'customer.account',
    navKey: 'account',
  },
  {
    path: '/account/notifications',
    screen: 'Notification preferences',
    permission: 'customer.notifications.manage',
    flag: 'customer.account',
    navKey: 'account',
  },
  {
    path: '/orders',
    screen: 'Order history',
    permission: 'customer.order.read',
    flag: 'checkout.payments',
    navKey: 'orders',
  },
  {
    path: '/orders/:orderId',
    screen: 'Order detail',
    permission: 'customer.order.read',
    flag: 'checkout.payments',
    navKey: 'orders',
  },
  {
    path: '/order-help',
    screen: 'Order help',
    permission: null,
    flag: null,
    navKey: 'orders',
  },
] as const;

/** Matches a customer pathname against the customer route table. */
export function resolveRoute(
  pathname: string,
  basePath: AppBasePath = customerBasePath,
): RouteMatch<RouteContract> | null {
  return resolveRouteIn(CUSTOMER_ROUTES, basePath.strip(pathname));
}
